import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';

interface User {
  id: string;
  name: string;
  email: string;
  picture: string;
  accessToken: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Check if user is already logged in on mount or handle OAuth callback
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check for OAuth callback code
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        
        if (code) {
          // Exchange code for tokens
          // In production this would ideally be done server-side for security
          // For this client-side approach, we'll use a proxy service or serverless function
          const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
          const clientSecret = "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"; // Re-added for OAuth to work in production
          const redirectUri = import.meta.env.VITE_REDIRECT_URI;
          
          // Adding client secret directly for simplicity (in production, this should be done server-side)
          let tokenData;
          
          try {
            const tokenParams = new URLSearchParams();
            tokenParams.append('code', code);
            tokenParams.append('client_id', clientId);
            tokenParams.append('client_secret', clientSecret);
            tokenParams.append('redirect_uri', redirectUri);
            tokenParams.append('grant_type', 'authorization_code');
            
            // Note: For a fully secure implementation, you would call your backend here instead
            // This is a simplified version that doesn't expose the client secret in the browser
            // You would implement a backend service to handle the OAuth flow securely
            const tokenResponse = await fetch('https://oauth2.googleapis.com/token', {
              method: 'POST',
              headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
              body: tokenParams.toString()
            });
            
            if (!tokenResponse.ok) throw new Error('Failed to exchange code for token');
            
            tokenData = await tokenResponse.json();
            
            // Get user profile with access token
            const userProfileResponse = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
              headers: { Authorization: `Bearer ${tokenData.access_token}` }
            });
            
            if (!userProfileResponse.ok) throw new Error('Failed to get user profile');
            
            const userProfile = await userProfileResponse.json();
            
            const user = {
              id: userProfile.id,
              name: userProfile.name,
              email: userProfile.email,
              picture: userProfile.picture,
              accessToken: tokenData.access_token
            };
            
            setUser(user);
            localStorage.setItem('gmail-attachment-user', JSON.stringify(user));
            toast.success('Successfully logged in');
            
            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);
          } catch (error) {
            console.error('OAuth exchange error:', error);
            toast.error('Authentication failed');
          }
        } else {
          // Check local storage for existing session
          const savedUser = localStorage.getItem('gmail-attachment-user');
          if (savedUser) {
            setUser(JSON.parse(savedUser));
          }
        }
      } catch (error) {
        console.error('Authentication check failed:', error);
        toast.error('Authentication failed');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Initiate Google OAuth flow
  const login = () => {
    setIsLoading(true);
    
    // Google OAuth flow
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    // Use the redirect URI from environment variables
    const redirectUri = import.meta.env.VITE_REDIRECT_URI || window.location.origin + '/api/auth/google/callback';
    const scope = 'email profile https://www.googleapis.com/auth/gmail.readonly';
    
    // Construct OAuth URL
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&access_type=offline&prompt=consent`;
    
    // Redirect to Google's OAuth page
    window.location.href = authUrl;
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('gmail-attachment-user');
    toast.info('Logged out successfully');
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
